/**
 * Noibu Feature Flag: quick add on search result cards
 * (flag "2083-quick-add-on-search-result-cards", Noibu A/B test 156).
 *
 * Replaces the test-153 wiring from PR #165 (flag
 * "2083-quick-add-on-search-results"): same idea, same files, now pointed at
 * draft 156, which is the test that will run. Only one of the two may run.
 *
 * Structurally parallel to assets/noibu-flag-sticky-checkout.js and
 * assets/noibu-flag-search.js. It is a separate file on purpose: those files
 * define the arms of running experiments and must not change mid-flight.
 *
 * The quick add buttons are server-rendered by sections/main-search.liquid
 * (Dawn's own card-product quick add, quick_add = standard in
 * templates/search.json) and hidden by sections/noibu-search-quick-add-ab.liquid
 * until this script says otherwise. A flag failure is therefore a no-op and
 * search renders exactly as it does today.
 *
 *   original                 -> html.nff-sqa--a  (control, the default)
 *   add-to-cart-on-results   -> html.nff-sqa--b
 *
 * In variation B every product card on /search shows Dawn's quick add button:
 * single-variant products add straight to the cart (cart notification opens),
 * multi-variant products open Dawn's options modal. Sold-out products show a
 * disabled "Sold out" button, as Dawn does on collections.
 *
 * Targeting lives in the Noibu test (all devices). There is deliberately no
 * CSS breakpoint: the flag decides who sees B; the CSS only obeys it.
 *
 * Noibu's implementation code for test 156 (console > A/B tests >
 * Implementation, read Nov 4 2026). The variation key was re-derived to
 * add-to-cart-on-results when variation B was renamed the same day:
 *
 *   function renderQuickAddButton(container) {
 *     // TODO: the implementation for variation B
 *   }
 *
 *   function renderOriginal(container) {
 *     // TODO: the implementation for the default experience
 *   }
 *
 *   function render(container) {
 *     const { variant } = client.getStringDetails(
 *       "2083-quick-add-on-search-result-cards",
 *       "original"
 *     );
 *
 *     switch (variant) {
 *       case "quick-add-button":   // now "add-to-cart-on-results"
 *         // Variation B
 *         return renderQuickAddButton(container);
 *       case "original":
 *         // Variation A (control)
 *       default:
 *         return renderOriginal(container);
 *     }
 *   }
 *
 *   // `client` is the ready client from the Noibu A/B testing SDK.
 *   // Call render(container) once your feature flag client is ready.
 *
 * SDK NOTE: the console snippet now destructures `variant`, which matches the
 * SDK. readVariation() still reads variant, then variation, then value, so an
 * older snippet shape cannot silently route everyone to the control.
 */
(function () {
  'use strict';

  var FLAG = '2083-quick-add-on-search-result-cards';
  var CONTROL = 'original';
  var VARIATION_B = 'add-to-cart-on-results';
  var CACHE_KEY = 'nff.search-quick-add-156';

  var REPORT_KEY = 'nff_search-quick-add';
  var DATA_ATTR = 'data-nff-sqa';
  var STATE_ATTR = 'data-nff-sqa-state';

  // Anchored to window.load because the client cannot arrive before it:
  // collect-core is injected by a ScriptTag on load, and collect-ff by
  // collect-core after that.
  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-sqa--a';
  CLASSES[VARIATION_B] = 'nff-sqa--b';

  var root = document.documentElement;

  function renderAddToCartOnResults() {
    // Nothing to build: the buttons are already in the server-rendered cards
    // (and in every grid facets.js re-renders after a filter or sort), and
    // html.nff-sqa--b makes them visible. Dawn's product-form.js and
    // quick-add.js, loaded by main-search, handle the add and the modal.
  }

  function renderOriginal() {
    // The control is what the server already renders with the buttons hidden.
  }

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }

    // The arm the page actually rendered, next to the SDK's own assignment
    // tuple, so a B that never showed can be spotted in the data.
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // collect-core may still be booting. Retry rather than drop the exposure.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(onUnavailable, 'no client ' + SDK_TIMEOUT_MS + 'ms after window.load');
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (!root.classList.contains(className)) {
      root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
      root.classList.add(className);
      root.setAttribute(DATA_ATTR, variation);

      document.dispatchEvent(
        new CustomEvent('noibu:search-quick-add-variation', { detail: { variation: variation } })
      );
    }

    if (variation === VARIATION_B) renderAddToCartOnResults();
    else renderOriginal();
  }

  function remember(variation) {
    try {
      window.localStorage.setItem(CACHE_KEY, variation);
    } catch (error) {
      /* no-op: private mode or an embedded webview */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_sqa=add-to-cart-on-results (or =original)
    try {
      var value = new URLSearchParams(window.location.search).get('nff_sqa');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable: fall through to the flag. */
    }
    return null;
  }

  function readVariation(client) {
    if (typeof client.getStringDetails === 'function') {
      var details = client.getStringDetails(FLAG, CONTROL) || {};
      return details.variant || details.variation || details.value || CONTROL;
    }
    return client.getStringValue(FLAG, CONTROL);
  }

  function render(client) {
    var variation = readVariation(client);

    switch (variation) {
      case VARIATION_B:
        activate(VARIATION_B);
        remember(VARIATION_B);
        return VARIATION_B;
      case CONTROL:
      // falls through
      default:
        // Control, visitors excluded by targeting, and unresolved flags.
        activate(CONTROL);
        remember(CONTROL);
        return CONTROL;
    }
  }

  var forced = forcedVariation();
  if (forced) {
    // Skips remember() and the SDK so a forced preview neither poisons the
    // visitor's cache nor records an exposure.
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));

      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      // Never remember() here: caching a value the SDK never produced would
      // replay a decision that was never made.
      if (!root.hasAttribute(DATA_ATTR)) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
